import { AavoDataGridMobileViewConfig, FlattenedAavoGridColDef } from "src/components/common/dataGrid/types.tsx";
import {
	GridColumnVisibilityModel,
	GridRenderCellParams,
	GridRowId,
	GridValidRowModel,
	useGridApiContext,
	useGridSelector,
} from "@mui/x-data-grid-pro";
import type { AavoDataGridProps } from "src/components/common/dataGrid/AavoDataGrid.tsx";
import { useContextMenu } from "src/components/common/contextMenu/useContextMenu.ts";
import { HorizontalBox } from "src/components/common/box/HorizontalBox.tsx";
import { Box, Typography } from "@mui/material";
import { VerticalBox } from "src/components/common/box/VerticalBox.tsx";
import { AavoButton } from "src/components/common/buttons/AavoButton.tsx";
import { faArrowUpRightFromSquare, faEllipsisVertical } from "@fortawesome/pro-regular-svg-icons";
import { AavoContextMenu } from "src/components/common/contextMenu/AavoContextMenu.tsx";
import { useGenericDialog } from "src/components/common/dialogs/GenericDialogContext.ts";
import React, { Fragment } from "react";
import { FormattedColumnValue } from "src/components/common/dataGrid/mobileView/FormattedColumnValue.tsx";
import { AavoDataGridMobileViewDetailsView } from "src/components/common/dataGrid/mobileView/AavoDataGridMobileViewDetailsView.tsx";
import { defineAgentUiContextKey } from "src/components/views/ai/assistant/uiContext/agentUiContextKey.ts";

export interface AavoDataGridMobileViewRowProps<RowData extends object>
	extends
		GridRenderCellParams<RowData>,
		Pick<
			AavoDataGridProps<RowData>,
			"listViewOnMobile" | "getRowId" | "rowContextMenuComponents" | "gridId" | "refreshData"
		> {
	columns: FlattenedAavoGridColDef<RowData>[];
}

export const AavoDataGridMobileViewRow = <RowData extends object>(props: AavoDataGridMobileViewRowProps<RowData>) => {
	const {
		columns,
		row,
		api,
		getRowId,
		rowContextMenuComponents,
		listViewOnMobile: listViewConfigProp,
		gridId,
		refreshData,
	} = props;

	const [openContextMenu, contextMenuState] = useContextMenu();
	const { openDialog } = useGenericDialog();
	const mobileViewConfig = typeof listViewConfigProp === "object" ? listViewConfigProp : undefined;
	const gridApiRef = useGridApiContext();
	const columnVisibilityModel = useGridSelector(
		gridApiRef,
		(apiRef) => apiRef.current.state.columns.columnVisibilityModel,
	);
	const visibleMobileColumns = getVisibleMobileColumns(columns, columnVisibilityModel);

	const rowId = getRowId(row);

	return (
		<HorizontalBox
			sx={{
				height: "100%",
				gap: 1,
			}}
		>
			<VerticalBox
				sx={{
					flex: 1,
					justifyContent: "center",
					gap: 0.5,
				}}
			>
				<RowHeader
					api={api}
					columns={visibleMobileColumns}
					rowId={rowId}
					mobileViewConfig={mobileViewConfig}
					row={row}
				/>
				<RowContent
					api={api}
					columns={visibleMobileColumns}
					rowId={rowId}
					columnVisibilityModel={columnVisibilityModel}
				/>
			</VerticalBox>
			{!mobileViewConfig?.hideOpenRowButton && (
				<AavoButton
					icon={faArrowUpRightFromSquare}
					onClick={(event) => {
						event.stopPropagation();
						openDetailsView();
					}}
				/>
			)}
			{rowContextMenuComponents != null && (
				<AavoButton
					icon={faEllipsisVertical}
					onClick={(event) => {
						event.stopPropagation();
						onContextMenu(event);
					}}
				/>
			)}
			<AavoContextMenu state={contextMenuState} />
		</HorizontalBox>
	);

	function openDetailsView() {
		openDialog({
			title: (
				<RowHeader
					api={api}
					columns={visibleMobileColumns}
					rowId={rowId}
					mobileViewConfig={mobileViewConfig}
					row={row}
					noWrapperForProvidedHeader
				/>
			),
			agentContext: {
				key: defineAgentUiContextKey("common.dataGridMobileDetails.dialog"),
				data: { gridId, rowId },
			},
			content: (
				<AavoDataGridMobileViewDetailsView
					row={row}
					rowId={rowId}
					api={api}
					columns={columns}
					detailsViewConfigProvider={mobileViewConfig?.detailsView}
					gridId={gridId}
					refreshData={refreshData}
				/>
			),
		});
	}

	function onContextMenu(event: React.MouseEvent) {
		if (rowContextMenuComponents == null) return;
		openContextMenu({
			mouseEvent: event,
			content: () =>
				rowContextMenuComponents({
					row: row,
					allSelectedRows: [row],
					multipleRowsSelected: false,
					onlySingleRowSelected: true,
				}),
		});
	}
};

interface RowHeaderProps<RowData extends GridValidRowModel> extends Pick<
	AavoDataGridMobileViewRowProps<RowData>,
	"api" | "columns" | "row"
> {
	rowId: GridRowId;
	mobileViewConfig: AavoDataGridMobileViewConfig<RowData> | undefined;
	noWrapperForProvidedHeader?: boolean;
}

const RowHeader = <RowData extends GridValidRowModel>({
	mobileViewConfig,
	columns,
	api,
	row,
	rowId,
	noWrapperForProvidedHeader,
}: RowHeaderProps<RowData>) => {
	if (mobileViewConfig?.header != null) {
		const providedHeader = mobileViewConfig.header(row);
		if (typeof providedHeader === "string" && !noWrapperForProvidedHeader) {
			return (
				<Typography
					sx={{
						fontWeight: "bold",
						fontSize: "1rem",
						overflow: "hidden",
						textOverflow: "ellipsis",
						whiteSpace: "nowrap",
					}}
				>
					{providedHeader}
				</Typography>
			);
		} else {
			return providedHeader;
		}
	}

	const headerColumns = columns.filter((column) => column.mobile === "header");

	return (
		<Box
			component={"span"}
			sx={{
				gap: 0.5,
				fontWeight: "bold",
				fontSize: "1rem",
				overflow: "hidden",
				textOverflow: "ellipsis",
				whiteSpace: "nowrap",
			}}
		>
			{headerColumns.map((column, index) => (
				<Fragment key={column.field}>
					{index > 0 && " | "}
					<FormattedColumnValue api={api} rowId={rowId} column={column} noTypographyWrapper />
				</Fragment>
			))}
		</Box>
	);
};

interface RowContentProps<RowData extends GridValidRowModel> extends Pick<
	AavoDataGridMobileViewRowProps<RowData>,
	"api" | "columns"
> {
	rowId: GridRowId;
	columnVisibilityModel: GridColumnVisibilityModel;
}

const RowContent = <RowData extends GridValidRowModel>({
	columns,
	api,
	rowId,
	columnVisibilityModel,
}: RowContentProps<RowData>) => {
	if (api.getRow(rowId) == null) return null;

	const cellColumns = columns.filter((column) => isColumnVisibleInCellMode(column, columnVisibilityModel));

	return cellColumns.map((column) => {
		const cellValue = api.getCellValue(rowId, column.field);
		const isMultiline = typeof cellValue === "string" && cellValue.includes("\n");

		return (
			<Box
				key={column.field}
				sx={{
					display: "flex",
					flexDirection: isMultiline ? "column" : "row",
					gap: 0.5,
					flexWrap: "wrap",
					alignItems: isMultiline ? "flex-start" : "center",
					"& .MuiTypography-root": {
						overflow: "hidden",
						fontSize: "0.9rem",
					},
				}}
			>
				<Typography>{column.headerName}: </Typography>
				<FormattedColumnValue
					api={api}
					rowId={rowId}
					column={column}
					typographySx={{ whiteSpace: "pre-wrap" }}
				/>
			</Box>
		);
	});
};

const isColumnVisibleInCellMode = <RowData extends GridValidRowModel>(
	column: FlattenedAavoGridColDef<RowData>,
	columnVisibilityModel: GridColumnVisibilityModel,
) => {
	if (column.mobile === "none") return false;
	if (column.mobile === "cell") return columnVisibilityModel[column.field] !== false;
	return columnVisibilityModel[column.field] === true;
};

const getVisibleMobileColumns = <RowData extends GridValidRowModel>(
	columns: FlattenedAavoGridColDef<RowData>[],
	columnVisibilityModel: GridColumnVisibilityModel,
) => {
	return columns.filter((column) => {
		if (column.mobile === "none") return false;
		return columnVisibilityModel[column.field] !== false;
	});
};
